/**
 * VoiceCommandListener.jsx
 * 2026-02 - Voice command global listener menggunakan Web Speech API.
 *
 * Cara kerja:
 * - Continuous listening dalam Bahasa Indonesia (id-ID), auto-restart on end.
 * - Saat user mengucapkan frasa command (mis. "tambah pelanggan"),
 *   dispatch CustomEvent `voice-command:add-customer` ke window.
 * - Halaman yang relevan (Customers.jsx) bisa subscribe ke event tsb
 *   untuk membuka dialog tambah pelanggan.
 * - Bila user sedang di luar /app/customers, akan auto-navigate dulu
 *   lalu trigger event setelah delay singkat.
 *
 * UI:
 * - Floating status pill kecil di kiri-bawah (kiri agar tidak bentrok
 *   dengan AutoRefreshControl/Toaster yang umumnya di kanan).
 * - Klik pill untuk toggle on/off, persisted di localStorage.
 * - Status: idle / listening / heard / error / disabled.
 *
 * Catatan:
 * - Web Speech API hanya tersedia di Chromium-based browser (Chrome/Edge).
 * - Mic permission akan diminta sekali oleh browser.
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Mic, MicOff } from 'lucide-react';
import { toast } from 'sonner';

const LS_KEY = 'rl_voice_command_enabled';

// Daftar frasa yang memicu command "tambah pelanggan".
// Normalisasi: lowercase, hilangkan tanda baca, single-space.
const ADD_CUSTOMER_PATTERNS = [
  'tambah pelanggan',
  'tambahkan pelanggan',
  'pelanggan baru',
  'buat pelanggan',
  'tambah customer',
  'add customer',
  'new customer',
];

// Frasa pencarian. Setelah verb+noun, sisanya = query.
// Contoh: "cari pelanggan budi" → query="budi"
//         "cari nama samiin"     → query="samiin"
const SEARCH_REGEXES = [
  /\b(?:cari|carikan|search|find|temukan|lihat|cek)\s+(?:pelanggan|customer|nama|user|client)\s+(.+)$/i,
  /\b(?:cari|carikan|search|find|temukan)\s+(.+)$/i, // fallback: "cari budi"
];

const normalize = (text) =>
  (text || '')
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const matchesAddCustomer = (text) => {
  // Pastikan tidak salah deteksi "cari pelanggan ..." sebagai tambah pelanggan
  if (/\b(cari|carikan|search|find|temukan|lihat|cek)\b/i.test(text)) return false;
  const n = normalize(text);
  return ADD_CUSTOMER_PATTERNS.some((p) => n.includes(p));
};

// Ekstrak query dari frasa "cari pelanggan X". Return null jika bukan command search.
const extractSearchQuery = (text) => {
  let t = (text || '').trim();
  if (!t) return null;
  // Normalisasi: ganti tanda baca tengah jadi spasi agar regex stabil.
  // Contoh: "cari pelanggan, budi." → "cari pelanggan budi"
  t = t.replace(/[,;:]+/g, ' ').replace(/\s+/g, ' ').trim();
  for (const re of SEARCH_REGEXES) {
    const m = t.match(re);
    if (m && m[1]) {
      let q = m[1].trim();
      q = q.replace(/[.!?,;:]+$/g, '').trim();
      q = q.replace(/^(dong|ya|deh|donk|tolong|please)\s+/i, '').trim();
      q = q.replace(/\s+(dong|ya|deh|donk|tolong|please)$/i, '').trim();
      // Hindari false positive untuk "cari pelanggan baru" / "tambah ..."
      if (/^baru$/i.test(q)) return null;
      // Buang prefix noun yang ikut tertangkap fallback (mis. "pelanggan budi")
      q = q.replace(/^(pelanggan|customer|nama|user|client)\s+/i, '').trim();
      if (q.length >= 2 && q.length <= 60) return q;
    }
  }
  return null;
};

const VoiceCommandListener = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Apakah browser support
  const SpeechRecognition =
    typeof window !== 'undefined'
      ? (window.SpeechRecognition || window.webkitSpeechRecognition)
      : null;
  const isSupported = !!SpeechRecognition;

  // User pref - default ON jika supported
  const [enabled, setEnabled] = useState(() => {
    try {
      const v = localStorage.getItem(LS_KEY);
      if (v === null) return true; // default on
      return v === '1';
    } catch {
      return true;
    }
  });

  const [status, setStatus] = useState('idle'); // idle | listening | heard | error | disabled
  const [lastHeard, setLastHeard] = useState('');

  const recognitionRef = useRef(null);
  const restartTimerRef = useRef(null);
  const manualStopRef = useRef(false);
  const lastCommandAtRef = useRef(0);
  // Buffer untuk menggabungkan beberapa segmen final dari Web Speech
  // (di Android Chrome, satu kalimat sering pecah jadi beberapa onresult).
  const transcriptBufferRef = useRef('');
  const bufferFlushTimerRef = useRef(null);

  const persistEnabled = (val) => {
    try {
      localStorage.setItem(LS_KEY, val ? '1' : '0');
    } catch {
      /* ignore */
    }
  };

  // Dispatch action berdasarkan frasa yang terdeteksi
  const handleCommand = useCallback(
    (transcript) => {
      // debounce 3 detik agar tidak double-fire
      const now = Date.now();
      if (now - lastCommandAtRef.current < 3000) return;

      // 1) Search Pelanggan — "cari pelanggan X" / "cari X"
      const searchQ = extractSearchQuery(transcript);
      if (searchQ) {
        lastCommandAtRef.current = now;
        setStatus('heard');
        toast.success(`Perintah suara: Cari "${searchQ}"`, {
          description: `Terdeteksi: "${transcript}"`,
          duration: 10000,
          closeButton: true,
        });

        const fire = () => {
          window.dispatchEvent(
            new CustomEvent('voice-command:search-customer', {
              detail: { query: searchQ },
            })
          );
        };

        if (!location.pathname.startsWith('/app/customers')) {
          navigate('/app/customers');
          setTimeout(fire, 600);
        } else {
          fire();
        }

        setTimeout(() => setStatus('listening'), 1500);
        return;
      }

      // 2) Tambah Pelanggan
      if (matchesAddCustomer(transcript)) {
        lastCommandAtRef.current = now;
        setStatus('heard');
        toast.success('Perintah suara: Tambah Pelanggan', {
          description: `Terdeteksi: "${transcript}"`,
          duration: 10000,
          closeButton: true,
        });

        const fire = () => {
          window.dispatchEvent(new CustomEvent('voice-command:add-customer'));
        };

        if (!location.pathname.startsWith('/app/customers')) {
          navigate('/app/customers');
          // Beri waktu komponen mount sebelum dispatch
          setTimeout(fire, 600);
        } else {
          fire();
        }

        // Kembalikan status ke listening setelah 1.5s
        setTimeout(() => setStatus('listening'), 1500);
        return;
      }

      // 3) Implicit search mode — saat user sedang di halaman /app/customers,
      //    terima kata pendek apa pun sebagai query pencarian (kompensasi
      //    untuk Web Speech Android Chrome yang sering hanya menangkap kata
      //    terakhir dari kalimat "cari pelanggan X").
      const trimmed = (transcript || '').trim();
      if (
        location.pathname.startsWith('/app/customers') &&
        trimmed.length >= 2 &&
        trimmed.length <= 30 &&
        // Jangan trigger kalau ada kata kerja command lain (mencegah ambigu)
        !/\b(tambah|tambahkan|buat|hapus|delete|update|edit|buka|open|isolir|enable|disable|aktif|matikan|nyalakan|stop|mulai|start)\b/i.test(trimmed)
      ) {
        // Filter stop-word Indonesia/Inggris yang sering muncul saat noise mic
        const stopWords = /^(ya|tidak|dan|atau|saya|kamu|dia|itu|ini|yang|udah|sudah|belum|nggak|gak|gk|ok|oke|halo|hai|test|tes|coba|ah|eh|uh|um|hmm|um|the|a|an|yes|no|please|ok|okay)$/i;
        const singleToken = trimmed.replace(/[.,!?]+$/g, '').trim();
        if (!stopWords.test(singleToken)) {
          // Implicit search
          lastCommandAtRef.current = now;
          setStatus('heard');
          const q = singleToken.replace(/[.!?,;:]+$/g, '').trim();
          toast.success(`Cari pelanggan: "${q}"`, {
            description: `Mode pencarian otomatis — terdeteksi: "${transcript}"`,
            duration: 10000,
            closeButton: true,
          });
          window.dispatchEvent(
            new CustomEvent('voice-command:search-customer', {
              detail: { query: q },
            })
          );
          setTimeout(() => setStatus('listening'), 1500);
          return;
        }
      }

      // 4) Tidak match command apapun — beri feedback singkat agar user tahu
      //    apa yang didengar oleh sistem. Hanya untuk transcript >= 4 char.
      if ((transcript || '').trim().length >= 4) {
        toast.info(`Didengar: "${transcript}"`, {
          description: 'Tidak match perintah. Coba: "Cari pelanggan [nama]" atau "Tambah pelanggan".',
          duration: 15000,
          closeButton: true,
        });
      }
    },
    [navigate, location.pathname]
  );

  // Start / stop recognition lifecycle
  useEffect(() => {
    if (!isSupported) {
      setStatus('disabled');
      return;
    }
    if (!enabled) {
      setStatus('disabled');
      // Stop running instance kalau ada
      if (recognitionRef.current) {
        manualStopRef.current = true;
        try {
          recognitionRef.current.stop();
        } catch {
          /* ignore */
        }
      }
      if (restartTimerRef.current) {
        clearTimeout(restartTimerRef.current);
        restartTimerRef.current = null;
      }
      return;
    }

    // Build recognition instance
    const rec = new SpeechRecognition();
    rec.lang = 'id-ID';
    rec.continuous = true;
    rec.interimResults = false;
    rec.maxAlternatives = 1;
    recognitionRef.current = rec;
    manualStopRef.current = false;

    rec.onstart = () => setStatus('listening');

    rec.onresult = (event) => {
      try {
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const res = event.results[i];
          if (res.isFinal) {
            const transcript = res[0]?.transcript || '';
            if (!transcript.trim()) continue;
            // Append ke buffer; tampilkan badge "last heard" dengan kumulatif.
            const buf = (transcriptBufferRef.current + ' ' + transcript).trim();
            transcriptBufferRef.current = buf;
            setLastHeard(buf);
            // eslint-disable-next-line no-console
            console.log('[Voice segment]', transcript, '| buffer:', buf);
            // Reset timer flush 1.5s (cukup untuk Android Chrome yang sering
            // memecah satu kalimat menjadi beberapa final result).
            if (bufferFlushTimerRef.current) {
              clearTimeout(bufferFlushTimerRef.current);
            }
            bufferFlushTimerRef.current = setTimeout(() => {
              const finalUtterance = transcriptBufferRef.current.trim();
              transcriptBufferRef.current = '';
              bufferFlushTimerRef.current = null;
              if (finalUtterance) {
                // eslint-disable-next-line no-console
                console.log('[Voice utterance]', finalUtterance);
                handleCommand(finalUtterance);
              }
            }, 1500);
          }
        }
      } catch (e) {
        /* ignore parsing error */
      }
    };

    rec.onerror = (event) => {
      // Error umum: no-speech, audio-capture, not-allowed, network
      const err = event?.error;
      if (err === 'not-allowed' || err === 'service-not-allowed') {
        setStatus('error');
        toast.error('Akses mikrofon ditolak. Voice command dinonaktifkan.', {
          description: 'Aktifkan izin mikrofon di pengaturan browser.',
        });
        setEnabled(false);
        persistEnabled(false);
        manualStopRef.current = true;
      } else if (err === 'audio-capture') {
        setStatus('error');
        toast.error('Mikrofon tidak terdeteksi. Voice command dinonaktifkan.');
        setEnabled(false);
        persistEnabled(false);
        manualStopRef.current = true;
      } else {
        // no-speech / network -> biarkan onend yg restart
        setStatus('idle');
      }
    };

    rec.onend = () => {
      if (manualStopRef.current) return;
      // Auto-restart agar continuous listening tetap jalan
      // (Chrome stop ~60s atau saat silence)
      restartTimerRef.current = setTimeout(() => {
        try {
          rec.start();
        } catch {
          // already started
        }
      }, 400);
    };

    try {
      rec.start();
    } catch {
      /* already started */
    }

    return () => {
      manualStopRef.current = true;
      if (restartTimerRef.current) {
        clearTimeout(restartTimerRef.current);
        restartTimerRef.current = null;
      }
      if (bufferFlushTimerRef.current) {
        clearTimeout(bufferFlushTimerRef.current);
        bufferFlushTimerRef.current = null;
      }
      transcriptBufferRef.current = '';
      try {
        rec.stop();
      } catch {
        /* ignore */
      }
      recognitionRef.current = null;
    };
  }, [enabled, isSupported, SpeechRecognition, handleCommand]);

  const toggle = () => {
    if (!isSupported) {
      toast.error('Browser ini tidak mendukung Web Speech API', {
        description: 'Gunakan Chrome / Edge versi terbaru.',
      });
      return;
    }
    const next = !enabled;
    setEnabled(next);
    persistEnabled(next);
    toast.info(next ? 'Voice command diaktifkan' : 'Voice command dinonaktifkan', {
      duration: 1800,
    });
  };

  // Tampilan pill kecil di kiri-bawah
  const isActive = enabled && (status === 'listening' || status === 'heard');
  const dotColor =
    status === 'heard'
      ? 'bg-emerald-400'
      : isActive
      ? 'bg-emerald-500 animate-pulse'
      : status === 'error'
      ? 'bg-red-500'
      : 'bg-slate-400';

  const label = !isSupported
    ? 'Voice N/A'
    : !enabled
    ? 'Voice Off'
    : status === 'heard'
    ? 'Terdengar'
    : status === 'listening'
    ? 'Voice Aktif'
    : status === 'error'
    ? 'Voice Error'
    : 'Voice Idle';

  return (
    <div className="fixed bottom-3 left-3 z-[60] flex items-end gap-2" style={{ userSelect: 'none' }}>
      <div
        data-testid="voice-command-indicator"
        onClick={toggle}
        title={
          isSupported
            ? `Voice Command (id-ID) — Klik untuk ${enabled ? 'matikan' : 'nyalakan'}.\nContoh: "Tambah Pelanggan" • "Cari Pelanggan Budi"${
                lastHeard ? `\nTerakhir: "${lastHeard}"` : ''
              }`
            : 'Browser tidak mendukung Web Speech API'
        }
        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-slate-900/85 hover:bg-slate-900 backdrop-blur-sm text-white text-[10px] font-medium shadow-lg cursor-pointer border border-white/10 transition-colors"
      >
        <span className={`inline-block w-1.5 h-1.5 rounded-full ${dotColor}`} />
        {enabled && isSupported ? (
          <Mic className="w-3 h-3" />
        ) : (
          <MicOff className="w-3 h-3" />
        )}
        <span className="hidden sm:inline">{label}</span>
      </div>

      {/* Last-heard transcript badge (persistent until next utterance, untuk debug & UX) */}
      {lastHeard && enabled && isSupported && (
        <div
          data-testid="voice-last-heard"
          title="Klik untuk hapus"
          onClick={() => setLastHeard('')}
          className="max-w-[280px] truncate px-2.5 py-1.5 rounded-full bg-indigo-600/90 hover:bg-indigo-600 backdrop-blur-sm text-white text-[10px] font-semibold shadow-lg cursor-pointer border border-white/10 transition-colors"
        >
          🎙️ &quot;{lastHeard}&quot;
        </div>
      )}
    </div>
  );
};

export default VoiceCommandListener;
